/* eslint-disable max-lines-per-function */
import { captureMessage } from '@sentry/browser';
import { MYOFFICE_VARIANTS } from 'Cloud/Application/Editor/MyOffice/myOffice.types';
import features from 'Cloud/Application/Features';
import { confirmActionWithSuspiciousItems } from 'Cloud/Application/Suspicious';
import config from 'Cloud/config';
import { path } from 'ramda';
import {
    IS_MOBILE_BROWSER,
    IS_MY_TEAM,
    IS_ONPREMISE,
    IS_PHONE_BROWSER,
    IS_PUBLIC_ALBUM,
    IS_REACT_PAGE,
} from 'reactApp/appHelpers/configHelpers';
import { isWopiEnabled } from 'reactApp/appHelpers/featuresHelpers';
import { EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { getSelectedFaceId } from 'reactApp/modules/faces/faces.selectors';
import { chooseVariant } from 'reactApp/modules/features/features.helpers';
import { getFeatureRequiredSignUpWhenDownloading } from 'reactApp/modules/features/features.selectors';
import { fileController } from 'reactApp/modules/file/file.controller';
import multiDownload, {
    downloadByIframe,
    downloadByLink,
    getIsItemVerifiedByKaspersky,
    isBlockedItem,
    isVirusItem,
} from 'reactApp/modules/file/utils';
import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import { authPopup } from 'reactApp/modules/ph/ph.thunkActions';
import { showVirusDlg } from 'reactApp/modules/popup/popup.module';
import { getPublicInfo } from 'reactApp/modules/public/public.actions';
import { getPublicRootWeblink } from 'reactApp/modules/public/public.selectors';
import { requiredAuthorizationHelpers } from 'reactApp/modules/requiredAuthorization/helpers';
import { reDownloadController } from 'reactApp/modules/requiredAuthorization/reDownloadController';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getSearchRequestParams } from 'reactApp/modules/search/search.selectors';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { getPageSubtitle } from 'reactApp/modules/storage/storage.helpers';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { ActionName } from 'reactApp/ui/VirusDialog/VirusDialog.types';
import { sendXray } from 'reactApp/utils/ga';
import { noop, noopVoid, parseURL } from 'reactApp/utils/helpers';
import { isEditingAttach, isEditingStorage } from 'reactApp/utils/isEditingFile';
import { sendKasperskiSnackAnalitics } from 'reactApp/utils/kasperskiSnackGa';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { sendEditRadar } from 'reactApp/utils/sendEditRadar';

const maxFilesCount = features.isFeature('multi-dwnld') && features.getFeatureParam('multi-dwnld', 'maxFilesCount', 10);

const getSnackbarErrorText = (storage, error) => {
    const isAlbum = storage === EStorageType.albums || IS_PUBLIC_ALBUM;

    if (isAlbum && error === 'CONDITION/NOENT') {
        return 'Невозможно скачать пустой альбом';
    }

    return 'Ошибка сервера';
};

function getSearchParams(item = {}, storage, isFolder) {
    const state = reduxStore.getState();
    const { query, xPageId, xReqId } = getSearchRequestParams(state);

    return {
        platform_place: IS_PHONE_BROWSER ? 'touch' : 'desktop',
        section: storage,
        search_phrase: query,
        name_files: item?.nameWithoutExt,
        type_content: item?.kind,
        page_id: xPageId,
        req_id: xReqId,
        pos: item.pos,
        hash: !isFolder ? item?.hash : '',
        size: !isFolder ? item?.size : null,
    };
}

// не даем скачивать убитый веблинк, который был доступен только на скачивание и счетчик скачиваний которого ушел в 0
const downloadIsNotAvaliable = () => {
    const state = reduxStore.getState();
    const rootWeblink = getPublicRootWeblink(state);
    const storage = getCurrentStorage(state);

    return (
        IS_ONPREMISE &&
        storage === EStorageType.public &&
        rootWeblink.count_downloads_left === 0 &&
        rootWeblink.count_downloads_total &&
        rootWeblink.count_downloads_total > 0 &&
        rootWeblink.weblinkDownloadable
    );
};

const showUnavaliableDownloadSnackbar = () => {
    snackbarController.showSnackbar({
        id: 'download-error',
        closable: true,
        text: 'Ссылка для скачивания больше недоступна',
        type: SnackbarTypes.failure,
    });
};

export default function Download(app, _) {
    const IS_MOBILE_CHROME = config.get('IS_MOBILE_CHROME');
    const MOBILE_BROWSER = config.get('MOBILE_BROWSER');
    const IS_IOS = config.get('IS_IOS');

    function download(url, downloadName, async, isForceDownloadByLink = false) {
        const state = reduxStore.getState();
        const rootWeblink = getPublicRootWeblink(state);
        const storage = getCurrentStorage(state);

        // в chrome на ios не работает скачивание просматриваемых файлов через iframe, поэтому скачиваем через ссылку
        if ((!async && IS_MOBILE_CHROME && (MOBILE_BROWSER === 'IOS' || IS_IOS)) || IS_MY_TEAM || isForceDownloadByLink) {
            if (!downloadName) {
                downloadName = parseURL(url).pathname.split('/').pop();
            }
            downloadByLink(url, downloadName);
        } else {
            downloadByIframe(url);
        }

        // идем в ручку /public/info, за получение актуальных значений счетчиков на скачивание
        if (
            storage &&
            storage === EStorageType.public &&
            IS_ONPREMISE &&
            rootWeblink.weblinkDownloadable &&
            rootWeblink.count_downloads_total &&
            rootWeblink.count_downloads_total > 0
        ) {
            reduxStore.dispatch(getPublicInfo());
        }
    }

    const downloadItem = function (url, name, storage, item, isForceDownloadByLink = false) {
        const state = reduxStore.getState();
        const isFaceFilterActive = Boolean(getSelectedFaceId(state));

        if (downloadIsNotAvaliable()) {
            return showUnavaliableDownloadSnackbar();
        }

        /* tempexp_15344-start */
        const isAuthorized = !!UserSelectors.getEmail(state);
        const authorizationRequired = app.isPublic() && !isAuthorized;

        const requiredSignUpForAllVariants = chooseVariant(getFeatureRequiredSignUpWhenDownloading, {
            control: false,
            variant1: authorizationRequired,
            variant2: authorizationRequired,
        })();

        if (requiredSignUpForAllVariants) {
            const successPage = requiredAuthorizationHelpers.getSuccessPage();

            reduxStore.dispatch(authPopup({ successPage, requiredAuthWhenDownload: 1 }));
            reDownloadController.resetDownloadHappened();
        } else {
            /* tempexp_15344-end */
            download(url, name, false, isForceDownloadByLink);

            sendPaymentGa({
                eventCategory: ECategoryGa.download,
                action: 'download-files',
                type_downloads: 'one-file',
                source: IS_PUBLIC_ALBUM ? 'album' : storage,
                place: isFaceFilterActive ? 'face_list' : '',
            });

            if (storage === 'search') {
                let searchParams = {};
                if (item && typeof item === 'object') {
                    searchParams = getSearchParams(item, storage, false);
                }
                sendPaymentGa({
                    eventCategory: ECategoryGa.downloadSearch,
                    action: 'download-files',
                    type_downloads: 'one-file',
                    source: IS_PUBLIC_ALBUM ? 'album' : storage,
                    place: isFaceFilterActive ? 'face_list' : '',
                    placement: item.srchSrc,
                    ...searchParams,
                });
            }

            sendXray(`app_download_${storage}`);

            if (
                /* tempexp_15344-next-line */
                chooseVariant(getFeatureRequiredSignUpWhenDownloading, {
                    control: (app.isPublic() || app.isAttaches()) && getIsItemVerifiedByKaspersky(state, item),
                    variant1: (app.isPublic() || app.isAttaches()) && isAuthorized,
                    variant2: (app.isPublic() || app.isAttaches()) && isAuthorized,
                })()
            ) {
                /* tempexp_15344-next-line */
                const runVariant = chooseVariant(
                    getFeatureRequiredSignUpWhenDownloading,
                    {
                        control: () => {
                            /**
                             * Показываем снэк только для пабликов и для аттачей(CLOUDWEB-14706)
                             * и самым последним этапом скачивания, что бы не мешался другим модалкам на тача
                             */
                            snackbarController.showSnackbar({
                                id: 'protected-download',
                                closable: true,
                                title: 'Скачивание началось',
                                text: IS_MOBILE_BROWSER
                                    ? 'Файл проверен Касперским и безопасен для вашего смартфона'
                                    : 'Файл проверен антивирусом Касперского и безопасен для вашего компьютера',
                                type: SnackbarTypes.protect,
                                onShow: () => {
                                    sendKasperskiSnackAnalitics({ action: 'view' });
                                },
                                onClose: () => {
                                    sendKasperskiSnackAnalitics({ action: 'close' });
                                },
                            });
                        },
                        variant1: () => {
                            reDownloadController.setDownloadHappened();
                            requiredAuthorizationHelpers.showAsideModal();
                        },
                        variant2: noopVoid,
                    },
                    {
                        skipCondition: {
                            forAll: () => !requiredAuthorizationHelpers.isSuccessAuthWhenDownload(state),
                        },
                    }
                )();

                runVariant();
            }
        }
    };

    Object.assign(app, {
        download(idOrItem, forceDownload, useDownloadUrl, storage, fromViewer) {
            const id = typeof idOrItem === 'object' ? idOrItem.id : idOrItem;

            let item = typeof idOrItem === 'object' ? idOrItem : null;

            storage = storage || item?.storage;

            const storageForRadar = IS_REACT_PAGE ? storage?.replace('/', '-') : _.sid().replace('/', '-');
            sendXray(`app_download-try_${storageForRadar}`);

            if (storage) {
                item = getStorageItemById(reduxStore.getState(), storage, id);
            } else if (!IS_REACT_PAGE) {
                item = this.get(idOrItem);
            } else if (!item) {
                item = getHomeItemById(reduxStore.getState(), id);
            }

            if (isBlockedItem(item)) {
                snackbarController.showSnackbar({
                    id: 'blocked',
                    text: 'Данный контент нарушает закон РФ и запрещён к распространению и использованию',
                    type: SnackbarTypes.failure,
                    closable: true,
                });
                return;
            }

            const isVirus = isVirusItem(item);

            if (isVirus && !forceDownload) {
                reduxStore.dispatch(showVirusDlg({ items: [item], doNotChangeRoute: fromViewer }));
                sendXray(`app_download_virus_${storageForRadar}`);
            } else {
                const download = item?.url?.download;
                const get = item?.url?.get;
                const isAttaches = storage === EStorageType.attaches || storage === EStorageType.viewerAttaches;
                const isForceDownloadByLink = storage === EStorageType.quotaCleaner;

                let url = ((useDownloadUrl || isAttaches) && download) || get;

                const isCloudAttachType = item?.type === EAttachTypes.cloud || item?.type === EAttachTypes.cloudStock;

                if (isCloudAttachType && item?.kind === 'image') {
                    url = (useDownloadUrl && download) || get;
                    sendXray(`download_attach_${item?.type}_type_${useDownloadUrl ? 'jpeg' : 'original'}`);
                }

                const state = reduxStore.getState();
                const storageFromState = getCurrentStorage(state);
                if (isEditingStorage(storageFromState)) {
                    sendEditRadar({
                        item,
                        storage: storageFromState,
                        i: 'download-content',
                        isAttach: isEditingAttach(storageFromState),
                        version: isWopiEnabled ? MYOFFICE_VARIANTS.wopi : MYOFFICE_VARIANTS.amr,
                    });
                }

                if (isVirus) {
                    // пользователь подтвердил скачивание
                    // зараженного файла из приватного облака
                    downloadItem(url, item.name, storageForRadar, item, isForceDownloadByLink);
                } else {
                    confirmActionWithSuspiciousItems(item, 'download')
                        .then(function () {
                            downloadItem(url, item.name, storageForRadar, item, isForceDownloadByLink);
                            // Отмена скачивания одного файла - информер не нужен
                        })
                        .catch((_) => noop);
                }
            }
        },

        downloadItems(name, idsOrItems, forceDownload) {
            const state = reduxStore.getState();
            const storage = getCurrentStorage(state);

            /* tempexp_15344-start */
            const isAuthorized = !!UserSelectors.getEmail(state);
            const authorizationRequired = app.isPublic() && !isAuthorized;

            if (
                chooseVariant(getFeatureRequiredSignUpWhenDownloading, {
                    control: false,
                    variant1: authorizationRequired,
                    variant2: authorizationRequired,
                })()
            ) {
                const successPage = requiredAuthorizationHelpers.getSuccessPage();

                reduxStore.dispatch(authPopup({ successPage: successPage.toString(), requiredAuthWhenDownload: 1 }));
                reDownloadController.resetDownloadHappened();
            } else {
                /* tempexp_15344-end */
                const storageForRadar = storage.replace('/', '-');
                const isFaceFilterActive = Boolean(getSelectedFaceId(state));
                sendXray(`app_zip-try_${storageForRadar}`);

                if (!Array.isArray(idsOrItems)) {
                    idsOrItems = [idsOrItems];
                }

                sendPaymentGa({
                    eventCategory: ECategoryGa.download,
                    action: 'download-files',
                    type_downloads: idsOrItems.length === 1 ? 'one-folder' : 'files',
                    source: IS_PUBLIC_ALBUM ? 'album' : storage,
                    place: isFaceFilterActive ? 'face_list' : '',
                    placement: idsOrItems.map((item) => item.srchSrc),
                    ...getSearchParams({ nameWithoutExt: name, kind: 'folder' }, storage, true),
                });

                if (storage === 'search') {
                    sendPaymentGa({
                        eventCategory: ECategoryGa.downloadSearch,
                        action: 'download-files',
                        type_downloads: idsOrItems.length === 1 ? 'one-folder' : 'files',
                        source: IS_PUBLIC_ALBUM ? 'album' : storage,
                        place: isFaceFilterActive ? 'face_list' : '',
                        ...getSearchParams({ nameWithoutExt: name, kind: 'folder' }, storage, true),
                    });
                }

                const originalItems = idsOrItems;

                if (storage === EStorageType.stock) {
                    name = getPageSubtitle();
                }

                const isVirus = IS_REACT_PAGE || app.isPublic() ? idsOrItems.some(isVirusItem) : app.isVirus(idsOrItems);

                function showVirusDialog() {
                    reduxStore.dispatch(showVirusDlg({ items: originalItems, actionName: ActionName.zip, name }));
                }

                if (isVirus && !forceDownload) {
                    showVirusDialog();
                    sendXray(`app_zip_virus_${storageForRadar}`);
                } else {
                    const makeZip = function (ids) {
                        fileController
                            .downloadFiles(ids, name, storage)
                            .then((url) => {
                                sendXray([`zip-download${storageForRadar}`, 'ok']);
                                download(url, `${name}.zip`, true);
                            })
                            .catch((error) => {
                                sendXray([`zip-download${storageForRadar}`, 'error']);
                                snackbarController.showSnackbar({
                                    id: 'apierror',
                                    text: getSnackbarErrorText(storage, error?.error),
                                    type: SnackbarTypes.failure,
                                    closable: true,
                                });
                            });
                    };

                    const items =
                        IS_REACT_PAGE || app.isPublic() || isFaceFilterActive
                            ? originalItems
                            : idsOrItems.map(function (itemId) {
                                  return app.get(itemId);
                              });

                    const originalItemCount = items.length;

                    /* tempexp_15344-next-line */
                    const runVariant = chooseVariant(
                        getFeatureRequiredSignUpWhenDownloading,
                        {
                            control: noopVoid,
                            variant1: () => {
                                reDownloadController.setDownloadHappened();
                                requiredAuthorizationHelpers.showAsideModal();
                            },
                            variant2: noopVoid,
                        },
                        { skipCondition: { forAll: () => !requiredAuthorizationHelpers.isSuccessAuthWhenDownload(state) } }
                    )();

                    confirmActionWithSuspiciousItems(items, 'download')
                        .then(function (items) {
                            if (IS_MOBILE_BROWSER && originalItemCount < maxFilesCount) {
                                const urlData = items
                                    .map((item) => ({
                                        url: path(['url', 'get'], item),
                                        name: path(['name'], item),
                                    }))
                                    .filter((item) => !!item.url);

                                sendXray(['download', 'multi', 'start']);

                                multiDownload(urlData)
                                    .then(() => {
                                        sendXray(['download', 'multi', 'ok']);

                                        /* tempexp_15344-next-line */
                                        runVariant();
                                    })
                                    .catch((error) => {
                                        sendXray(['download', 'multi', 'error']);
                                        snackbarController.showSnackbar({
                                            id: 'cancel',
                                            text: 'Не удалось скачать файлы',
                                            type: SnackbarTypes.failure,
                                            closable: true,
                                        });
                                        captureMessage('download multi exception', { extra: error });
                                    });
                            } else {
                                if (downloadIsNotAvaliable()) {
                                    return showUnavaliableDownloadSnackbar();
                                }

                                const ids = items.map((item) => item?.id || '').filter(Boolean);

                                makeZip(ids);

                                /* tempexp_15344-next-line */
                                runVariant();
                            }
                        })
                        .catch(function () {
                            if (originalItemCount > 1) {
                                snackbarController.showSnackbar({
                                    id: 'cancel',
                                    text: 'Вы отменили скачивание всех файлов',
                                    type: SnackbarTypes.failure,
                                    closable: true,
                                });
                            }
                        });
                }
            }
        },

        downloadItem,
    });
}
